import { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import {
  LucideMail,
  LucidePhone,
  LucideSmartphone,
  LucideUser,
} from 'lucide-react'
import { ListingCardBase } from '~/components'
import { Button } from '~/components/ui'
import { Company, Listing, User } from '~/models'
import {
  activityHelper,
  findCompany,
  findListings,
  findUsers,
} from '~/mongo.server'
import { fmtAddress, fmtName, fmtPhoneNumber } from '~/utils'
import { getOrganizationSlug } from '~/utils.server'

type LoaderData = {
  company: Company
  broker: User
  listing: Listing
}

export const loader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<{
  company: Company
  brokers: User[]
  listings: Listing[]
}> => {
  const slug = params.slug
  if (!slug) {
    throw new Response('Listing Not found', { status: 404 })
  }
  const organizationSlug = getOrganizationSlug(request)
  const company = await findCompany({
    slug,
    'organization.slug': organizationSlug,
  })
  if (!company) {
    throw new Response('Not Found', { status: 404 })
  }

  const brokers = await findUsers({
    'company._id': company._id,
    'organization.slug': organizationSlug,
    status: 'active',
    role: { $in: ['broker', 'managingBroker'] },
  })
  const listingsResult = await findListings({
    find: {
      'company._id': company._id,
      'organization.slug': organizationSlug,
      status: { $in: ['active'] },
    },
    limit: 9,
  })
  if (!listingsResult) {
    throw new Response('Not Found', { status: 404 })
  }
  await activityHelper(request)
    .source('app')
    .type('view')
    .related('company', company)
    .write()
  return { company, brokers, listings: listingsResult.listings }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title = data?.company.name
  if (title) {
    return [
      {
        title: `${title} - Associate`,
      },
    ]
  }
  return []
}

export default function AppCompanyDetailRoute() {
  const { company, brokers, listings } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-[500px]">
      <div className="mx-auto mb-16 max-w-6xl px-4 pt-8 sm:px-6 md:pt-16">
        <div className="flex flex-col gap-8 md:flex-row lg:gap-16">
          <div className="flex-1">
            {company.meta.logo?.url && (
              <div className="mb-10 max-w-[200px] overflow-hidden rounded-md">
                <img src={company.meta.logo?.url} alt="Company Logo"></img>
              </div>
            )}
            <h1 className="mb-6 text-2xl tracking-tight sm:text-4xl">
              {company.name}
            </h1>
            <dl className="grid gap-6 sm:grid-cols-2 md:grid-cols-4">
              {company.meta.address && (
                <div className="">
                  <dt className="mb-1 font-medium text-muted-foreground">
                    Address
                  </dt>
                  <dd className="whitespace-pre-line">
                    {fmtAddress(company.meta)}
                  </dd>
                </div>
              )}
              {company.meta.website && (
                <div className="">
                  <dt className="mb-1 font-medium text-muted-foreground">
                    Website
                  </dt>
                  <dd className="">
                    <Button
                      asChild
                      variant="link"
                      className="px-0 text-base font-normal">
                      <a
                        href={company.meta.website}
                        target="_blank"
                        className="text-primary">
                        {company.meta.website}
                      </a>
                    </Button>
                  </dd>
                </div>
              )}
              {company.meta.email && (
                <div className="">
                  <dt className="mb-1 font-medium text-muted-foreground">
                    Email
                  </dt>
                  <dd className="flex items-center">
                    <LucideMail className="mr-3 h-5 w-5 text-foreground" />
                    <Button
                      asChild
                      variant="link"
                      className="px-0 text-base font-normal">
                      <a href={`mailto:${company.meta.email}`} target="_blank">
                        {company.meta.email}
                      </a>
                    </Button>
                  </dd>
                </div>
              )}
              {company.meta.phone && (
                <div className="">
                  <dt className="mb-1 font-medium text-muted-foreground">
                    Phone
                  </dt>
                  <dd className="flex items-center">
                    <LucideSmartphone className="mr-3 h-5 w-5 text-foreground" />
                    <Button
                      asChild
                      variant="link"
                      className="px-0 text-base font-normal">
                      <a href={`tel:${company.meta?.phone}`} target="_blank">
                        {fmtPhoneNumber(company.meta?.phone)}
                      </a>
                    </Button>
                  </dd>
                </div>
              )}
            </dl>

            {brokers.length > 0 && (
              <Button asChild variant="link" className="mt-4 px-0 text-base">
                <a href="#brokers">View Brokers</a>
              </Button>
            )}
          </div>
        </div>
      </div>

      {listings.length > 0 && (
        <div className="bg-muted px-4 py-16 md:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-16 text-center text-2xl sm:text-3xl">
              Latest Listings
            </h2>
            <div className="max-h-auto grid grid-cols-1 place-content-start gap-4 overflow-hidden md:grid-cols-2 lg:grid-cols-3">
              {listings.map((listing) => {
                return (
                  <ListingCardBase
                    key={listing._id}
                    listing={listing}
                    showCompany={false}
                  />
                )
              })}
            </div>
            <div className="my-8 flex justify-center">
              <Button>
                <Link to={`/cards?companies=${company.tid}`}>
                  View All Listings
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
      {brokers.length > 0 && (
        <div id="brokers" className="bg-muted px-4 py-16 md:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-16 mt-10 text-center text-2xl sm:text-3xl">
              Meet the Team
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {brokers
                .filter((broker) => broker.status === 'active')
                .sort(function (brokerA, brokerB) {
                  if (brokerA.firstName > brokerB.firstName) {
                    return 1
                  }
                  if (brokerA.firstName < brokerB.firstName) {
                    return -1
                  }
                  return 0
                })
                .map((broker) => {
                  return (
                    <div
                      key={broker.tid}
                      className="rounded-md bg-background/50 p-6 shadow-sm sm:p-8">
                      <div className="mx-16 mb-6 flex aspect-square items-center justify-center rounded-full bg-muted shadow-sm">
                        {broker.meta?.photo ? (
                          <img
                            src={broker.meta.photo.url}
                            className="h-full w-full rounded-full object-cover"
                          />
                        ) : (
                          <LucideUser className="h-20 w-20 text-muted-foreground" />
                        )}
                      </div>
                      <h3 className="mb-4 text-lg">
                        <Link
                          to={`/brokers/${broker.slug}`}
                          className="hover:underline hover:underline-offset-4">
                          {fmtName(
                            broker.firstName,
                            broker.middleName,
                            broker.lastName
                          )}
                        </Link>
                        {broker.meta?.memberships && (
                          <span className="ml-2 text-base text-muted-foreground">
                            {broker.meta.memberships
                              .split(/\s+|,\s+|\|/)
                              .join(', ')}
                          </span>
                        )}
                      </h3>
                      {broker.meta?.description && (
                        <div
                          className="mb-4 line-clamp-4"
                          dangerouslySetInnerHTML={{
                            __html: broker.meta!.description,
                          }}></div>
                      )}
                      {broker.email && (
                        <div className="flex items-center">
                          <LucideMail className="mr-3 h-5 w-5 text-foreground" />
                          <Button
                            asChild
                            variant="link"
                            className="px-0 text-base font-normal">
                            <a href={`mailto:${broker.email}`} target="_blank">
                              {broker.email}
                            </a>
                          </Button>
                        </div>
                      )}
                      {broker.meta?.office && (
                        <div className="flex items-center">
                          <LucidePhone className="mr-3 h-5 w-5 text-foreground" />
                          <Button
                            asChild
                            variant="link"
                            className="px-0 text-base font-normal">
                            <a
                              href={`tel:${broker.meta?.office}`}
                              target="_blank">
                              {fmtPhoneNumber(broker.meta?.office)}
                            </a>
                          </Button>
                        </div>
                      )}
                      {broker.meta?.mobile && (
                        <div className="flex items-center">
                          <LucideSmartphone className="mr-3 h-5 w-5 text-foreground" />
                          <Button
                            asChild
                            variant="link"
                            className="px-0 text-base font-normal">
                            <a
                              href={`tel:${broker.meta?.mobile}`}
                              target="_blank">
                              {fmtPhoneNumber(broker.meta?.mobile)}
                            </a>
                          </Button>
                        </div>
                      )}
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
